import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit2, X, Search, Filter, ChevronDown } from 'lucide-react';
import AdminLayout from './AdminLayout';
import '../styles/Admin.css';
import { getAllServices } from '../ServiceAPI/AdminUser/serviceapi';
import { getStates } from '../ServiceAPI/master/api';
import {
  getAllServiceStateAccess,
  createServiceStateAccess,
  updateServiceStateAccess,
  deleteServiceStateAccess
} from '../ServiceAPI/AdminUser/serviceStateAccessApi';

const emptyForm = {
  Service_id: '',
  State_id: '',
  Status: 1,
  Gov_fee: '',
  Service_fee: '',
  Effective_from: '',
  Effective_to: ''
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `₹${Number(value).toFixed(2)}`;
};

const calcTotalFee = (govFee, serviceFee) => {
  const gov = Number(govFee);
  const service = Number(serviceFee);
  if (!Number.isFinite(gov) || !Number.isFinite(service)) return '—';
  return formatCurrency(gov + service);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN');
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const SearchableSelect = ({ label, name, value, options, placeholder, onChange, required, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const selected = options.find((opt) => String(opt.value) === String(value));
  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(name, optionValue);
    setOpen(false);
    setSearch('');
  };

  if (disabled) {
    return (
      <div className="form-group-modern">
        <label>{label}</label>
        <input type="text" value={selected?.label || '—'} readOnly className="readonly-field" />
      </div>
    );
  }

  return (
    <div
      className={`form-group-modern admin-searchable-select${open ? ' open' : ''}`}
      ref={containerRef}
    >
      <label>{label}{required ? ' *' : ''}</label>
      <button
        type="button"
        className={`admin-searchable-select-trigger${selected ? '' : ' placeholder'}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{selected ? selected.label : placeholder}</span>
        <ChevronDown size={18} />
      </button>
      {open && (
        <div className="admin-searchable-select-menu">
          <input
            type="text"
            className="admin-searchable-select-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}...`}
            autoFocus
          />
          <ul className="admin-searchable-select-list" role="listbox">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  role="option"
                  className={`admin-searchable-select-option${String(opt.value) === String(value) ? ' selected' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </li>
              ))
            ) : (
              <li className="admin-searchable-select-empty">No matches found</li>
            )}
          </ul>
        </div>
      )}
      {required && (
        <input
          type="text"
          name={name}
          value={value || ''}
          required
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
          onChange={() => {}}
        />
      )}
    </div>
  );
};

const ServiceStateAccessManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    const isAdminAuthenticated = localStorage.getItem('adminAuth') === 'true';
    if (!isAdminAuthenticated) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [servicesRes, statesRes, accessRes] = await Promise.all([
        getAllServices(),
        getStates(),
        getAllServiceStateAccess()
      ]);

      setServices(Array.isArray(servicesRes?.data) ? servicesRes.data : []);
      setStates(Array.isArray(statesRes?.data) ? statesRes.data : []);
      setRecords(Array.isArray(accessRes?.data) ? accessRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const serviceOptions = useMemo(() => {
    return services.map((service) => ({
      value: service.Id,
      label: `${service.Name} > ${service.Type} > ${service.Category}`
    }));
  }, [services]);

  const stateOptions = useMemo(() => {
    return states.map((state) => ({
      value: state.Id,
      label: state.Name
    }));
  }, [states]);

  const filteredRecords = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return records.filter((row) => {
      const matchesSearch =
        !searchTerm ||
        (row.Service_name && row.Service_name.toLowerCase().includes(searchLower)) ||
        (row.Service_type && row.Service_type.toLowerCase().includes(searchLower)) ||
        (row.Service_category && row.Service_category.toLowerCase().includes(searchLower)) ||
        (row.State_name && row.State_name.toLowerCase().includes(searchLower)) ||
        (row.Gov_fee !== null && row.Gov_fee !== undefined && String(row.Gov_fee).includes(searchTerm)) ||
        (row.Service_fee !== null && row.Service_fee !== undefined && String(row.Service_fee).includes(searchTerm)) ||
        (row.Total_fee !== null && row.Total_fee !== undefined && String(row.Total_fee).includes(searchTerm));

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && Number(row.Status) === 1) ||
        (statusFilter === 'inactive' && Number(row.Status) === 0);

      return matchesSearch && matchesStatus;
    });
  }, [records, searchTerm, statusFilter]);

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingRecord(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingRecord(row);
    setFormData({
      Service_id: row.Service_id,
      State_id: row.State_id,
      Status: Number(row.Status),
      Gov_fee: row.Gov_fee ?? '',
      Service_fee: row.Service_fee ?? '',
      Effective_from: toDateInputValue(row.Effective_from),
      Effective_to: toDateInputValue(row.Effective_to)
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.Gov_fee === '' || Number(formData.Gov_fee) < 0) {
      setError('Gov fee is required and must be zero or greater');
      return;
    }
    if (formData.Service_fee === '' || Number(formData.Service_fee) < 0) {
      setError('Service fee is required and must be zero or greater');
      return;
    }

    try {
      setSaving(true);

      if (editingRecord) {
        await updateServiceStateAccess(editingRecord.Id, {
          Status: Number(formData.Status),
          Gov_fee: Number(formData.Gov_fee),
          Service_fee: Number(formData.Service_fee),
          Effective_from: formData.Effective_from || null,
          Effective_to: formData.Effective_to || null
        });
        setSuccess('Record updated successfully');
      } else {
        await createServiceStateAccess({
          Service_id: Number(formData.Service_id),
          State_id: Number(formData.State_id),
          Status: Number(formData.Status),
          Gov_fee: Number(formData.Gov_fee),
          Service_fee: Number(formData.Service_fee),
          Effective_from: formData.Effective_from || null,
          Effective_to: formData.Effective_to || null
        });
        setSuccess('Service state access and fee created successfully');
      }

      closeModal();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this access and its fee mapping?')) return;
    try {
      await deleteServiceStateAccess(id);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete record');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading service state access...</p>
        </div>
      </AdminLayout>
    );
  }

  const isEditing = Boolean(editingRecord);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Service State Access</h1>
          <p className="admin-page-subtitle">Map services to states and set fees</p>
        </div>
        <button type="button" className="btn-primary-modern" onClick={openCreateModal}>
          <Plus size={20} />
          Add Access
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="alert-close">
            <X size={16} />
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="alert-close">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="admin-content-area">
        <div className="data-table-card">
          <div className="data-table-header">
            <h3>Service State Access ({filteredRecords.length} of {records.length})</h3>
          </div>

          <div className="search-filter-section">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search by service, state, or fee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <Filter size={18} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Service</th>
                  <th>State</th>
                  <th>Gov Fee</th>
                  <th>Service Fee</th>
                  <th>Total Fee</th>
                  <th>Effective From</th>
                  <th>Effective To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((row) => (
                    <tr key={row.Id}>
                      <td><span className="td-badge">{row.Id}</span></td>
                      <td>
                        <div className="service-info-cell">
                          <strong>{row.Service_name}</strong>
                          <small>{row.Service_type} | {row.Service_category}</small>
                        </div>
                      </td>
                      <td><strong>{row.State_name}</strong></td>
                      <td>{formatCurrency(row.Gov_fee)}</td>
                      <td>{formatCurrency(row.Service_fee)}</td>
                      <td><strong>{formatCurrency(row.Total_fee ?? (Number(row.Gov_fee) + Number(row.Service_fee)))}</strong></td>
                      <td>{formatDate(row.Effective_from)}</td>
                      <td>{formatDate(row.Effective_to)}</td>
                      <td>
                        <span className={`status-badge-modern ${Number(row.Status) === 1 ? 'active' : 'inactive'}`}>
                          {Number(row.Status) === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="table-action-group">
                          <button
                            type="button"
                            className="action-btn primary"
                            onClick={() => openEditModal(row)}
                            title="Edit status & fee"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            type="button"
                            className="action-btn danger"
                            onClick={() => handleDelete(row.Id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="empty-cell">
                      <div className="empty-state">
                        <Plus size={48} />
                        <p>No service state access records found</p>
                        <button type="button" className="btn-secondary-modern" onClick={openCreateModal}>
                          Add first access
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay-modern">
          <div className="modal-container-modern">
            <div className="modal-header-modern">
              <h3>{isEditing ? 'Edit Service State Access' : 'Add Service State Access'}</h3>
              <button type="button" className="modal-close-btn" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form-modern">
              <SearchableSelect
                label="Service"
                name="Service_id"
                value={formData.Service_id}
                options={serviceOptions}
                placeholder="Select service"
                onChange={handleSelectChange}
                required={!isEditing}
                disabled={isEditing}
              />

              <SearchableSelect
                label="State"
                name="State_id"
                value={formData.State_id}
                options={stateOptions}
                placeholder="Select state"
                onChange={handleSelectChange}
                required={!isEditing}
                disabled={isEditing}
              />

              <div className="form-row">
                <div className="form-group-modern">
                  <label>Gov Fee *</label>
                  <input
                    type="number"
                    name="Gov_fee"
                    min="0"
                    step="0.01"
                    value={formData.Gov_fee}
                    onChange={handleInputChange}
                    placeholder="Government fee"
                    required
                  />
                </div>
                <div className="form-group-modern">
                  <label>Service Fee *</label>
                  <input
                    type="number"
                    name="Service_fee"
                    min="0"
                    step="0.01"
                    value={formData.Service_fee}
                    onChange={handleInputChange}
                    placeholder="Service fee"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group-modern">
                  <label>Total Fee</label>
                  <input
                    type="text"
                    value={calcTotalFee(formData.Gov_fee, formData.Service_fee)}
                    readOnly
                    className="readonly-field"
                  />
                  <small className="form-hint">Auto-calculated (Gov fee + Service fee)</small>
                </div>
                <div className="form-group-modern checkbox-group" style={{ justifyContent: 'flex-end' }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="Status"
                      checked={Number(formData.Status) === 1}
                      onChange={handleInputChange}
                    />
                    <span>Active status</span>
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group-modern">
                  <label>Effective From</label>
                  <input
                    type="date"
                    name="Effective_from"
                    value={formData.Effective_from}
                    onChange={handleInputChange}
                  />
                  <small className="form-hint">Optional</small>
                </div>
                <div className="form-group-modern">
                  <label>Effective To</label>
                  <input
                    type="date"
                    name="Effective_to"
                    value={formData.Effective_to}
                    onChange={handleInputChange}
                    min={formData.Effective_from || undefined}
                  />
                  <small className="form-hint">Optional</small>
                </div>
              </div>

              {isEditing && (
                <p className="form-hint" style={{ marginTop: 0 }}>
                  Service and state cannot be changed. Only status and fee details are editable.
                </p>
              )}

              <div className="modal-footer-modern">
                <button type="button" className="btn-secondary-modern" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary-modern" disabled={saving}>
                  {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ServiceStateAccessManagement;
